import { ApplicationTypeEnum, PropertyTypeEnum } from "@lib/api/enums";
import { ApplicationFormValues } from "@lib/mapApplicationData/types";
import i18n from "i18next";
import {
  FormDefinition,
  FormFieldDefinition,
} from "../../components/DynamicForm";
import {
  getMinMaxDateInput,
  getRealEstateAgentCommissionDefaultValue,
  validateDateInput,
  validateZip,
} from "../utilities";
import { OTHER } from "./types";

type TFunction = typeof i18n.t;

export const getApplicationTypeStep = (
  t: TFunction,
  otherPath: string,
): FormDefinition<ApplicationFormValues> => {
  return {
    title: t("zero_step_title"),
    fields: [
      {
        component: "TOGGLE_BUTTON",
        name: "applicationType",
        options: [
          {
            value: ApplicationTypeEnum.BUY_WITH_SAVINGS,
            label: t("compare_buy_with_savings"),
            image: "/assets/img/house.svg",
            columns: 4,
          },

          {
            value: ApplicationTypeEnum.BUILD_HOUSE,
            label: t("compare_build_a_house"),
            image: "/assets/img/build-a-house.svg",
            columns: 4,
          },

          {
            value: ApplicationTypeEnum.REFINANCING,
            label: t("compare_refinancing"),
            image: "/assets/img/refinance.svg",
            columns: 4,
          },
          {
            value: OTHER,
            href: otherPath,
            label: t("compare_other"),
            columns: 12,
          },
        ],
      },
    ],
  };
};

export const getTypeOfPropertyStep = (
  t: TFunction,
  otherHref: string,
): FormDefinition<ApplicationFormValues> => {
  return {
    title: t("type_of_property_title"),
    fields: [
      {
        component: "TOGGLE_BUTTON",
        name: "typeOfProperty",
        options: [
          {
            value: PropertyTypeEnum.APARTMENT,
            label: t("type_of_property_apartment"),
            image: "/assets/img/apartment.svg",
          },
          {
            value: PropertyTypeEnum.SINGLE_FAMILY_HOUSE,
            image: "/assets/img/house.svg",
            label: t("type_of_property_house"),
          },
          {
            value: PropertyTypeEnum.APARTMENT_BUILDING,
            image: "/assets/img/apartment-building.svg",
            label: t("type_of_property_apartment_building"),
          },
          {
            value: PropertyTypeEnum.LAND_PLOT,
            image: "/assets/img/landplot.svg",
            label: t("type_of_property_land_plot"),
          },
          {
            value: OTHER,
            href: otherHref,
            label: t("type_of_property_other"),
            columns: 12,
          },
        ],
      },
    ],
  };
};

export const getTypeOfBuildingStep = (
  t: TFunction,
): FormDefinition<ApplicationFormValues> => {
  return {
    title: t("type_of_property_title_build_a_house"),
    fields: [
      {
        component: "TOGGLE_BUTTON",
        name: "typeOfProperty",
        options: [
          {
            value: PropertyTypeEnum.SINGLE_FAMILY_HOUSE,
            image: "/assets/img/house.svg",
            label: t("type_of_property_house"),
            columns: 4,
          },
          {
            value: PropertyTypeEnum.TOWNHOUSE,
            image: "/assets/img/townhouse.svg",
            label: t("type_of_property_townhouse"),
            columns: 4,
          },
          {
            value: PropertyTypeEnum.APARTMENT_BUILDING,
            image: "/assets/img/apartment-building.svg",
            label: t("type_of_property_apartment_building"),
            columns: 4,
          },
        ],
      },
    ],
  };
};

export const getWhereToBuyStep = (
  t: TFunction,
): FormDefinition<ApplicationFormValues> => {
  return {
    submit: true,
    title: t("where_to_buy_title"),
    description: t("where_to_buy_description"),
    fields: [
      {
        label: t("zip_code_where_to_buy_label"),
        placeholder: t("zip_code_placeholder"),
        component: "INPUT",
        name: "postalCode",
        type: "text",
        inputMode: "numeric",
        rules: {
          required: {
            value: true,
            message: t("error_postal_code_required"),
          },
          minLength: {
            value: 5,
            message: t("error_postal_code_format"),
          },
          maxLength: {
            value: 5,
            message: t("error_postal_code_format"),
          },
          validate: validateZip,
        },
      },
    ],
  };
};

export const getPurchasePriceStep = (
  t: TFunction,
  skipRealEstateAgentFee: boolean,
): FormDefinition<ApplicationFormValues> => {
  let description = t("down_payment_description");
  const fields: FormFieldDefinition<ApplicationFormValues>[] = [
    {
      label: t("purchase_price_label"),
      placeholder: t("purchase_price_placeholder"),
      component: "NUMBER_INPUT",
      inputMode: "numeric",
      name: "purchasePrice",
      suffix: "€",
      rules: {
        required: { value: true, message: t("error_purchase_price") },
        min: { value: 5000, message: t("error_purchase_price") },
      },
    },
  ];

  if (!skipRealEstateAgentFee) {
    description += " " + t("real_estate_agent_commission_description");
    fields.push({
      component: "SWITCH",
      name: "hasRealEstateAgentCommission",
      label: t("has_real_estate_agent_commission_title"),
      defaultValue: false,
    });

    fields.push({
      label: t("real_estate_agent_commission_label"),
      info: t("real_estate_agent_commission_info"),
      component: "NUMBER_INPUT",
      condition: (value: any) => value.hasRealEstateAgentCommission,
      name: "realEstateAgentCommission",
      placeholder: "0",
      suffix: "%",
      defaultValue: getRealEstateAgentCommissionDefaultValue,
      rules: {
        required: {
          message: t("error_real_estate_agent_commission"),
        },
        min: 0,
        max: 9,
      },
    });
  }

  return {
    submit: true,
    title: t("purchase_price_title"),
    description,
    fields,
  };
};

export const getDownPaymentStep = (
  t: TFunction,
): FormDefinition<ApplicationFormValues> => {
  return {
    submit: true,
    title: t("down_payment_title"),
    description: t("down_payment_description"),
    fields: [
      {
        label: t("down_payment_label"),
        placeholder: t("down_payment_placeholder"),
        component: "NUMBER_INPUT",
        name: "downPayment",
        suffix: "€",
        inputMode: "numeric",
        rules: {
          required: { value: true, message: t("error_down_payment") },
          min: 0,
        },
      },
      {
        component: "SWITCH",
        name: "wantsToSellAProperty",
        label: t("wants_to_sell_another_property"),
        defaultValue: false,
      },
    ],
  };
};

export const getMonthlyNetIncomeStep = (
  t: TFunction,
): FormDefinition<ApplicationFormValues> => {
  return {
    submit: true,
    title: t("monthly_net_income_title"),
    description: t("monthly_net_income_description"),
    fields: [
      {
        label: t("monthly_net_income_label"),
        placeholder: t("monthly_net_income_placeholder"),
        component: "NUMBER_INPUT",
        name: "monthlyNetIncome",
        inputMode: "numeric",
        suffix: "€",
        rules: {
          required: { value: true, message: t("error_monthly_net_income") },
          min: 1800,
        },
      },
    ],
  };
};

export const getHaveFoundLandplotStep = (
  t: TFunction,
): FormDefinition<ApplicationFormValues> => {
  return {
    title: t("have_found_landplot_title"),
    fields: [
      {
        component: "TOGGLE_BUTTON",
        name: "ownsLandplot",
        isOneColumns: true,
        options: [
          {
            value: true,
            image: "/assets/img/simple-house.svg",
            label: t("have_found_landplot_field_1_label"),
          },
          {
            value: false,
            image: "/assets/img/simple-house-euro.svg",
            label: t("have_found_landplot_field_2_label"),
          },
        ],
      },
    ],
  };
};

export const getWhatMarketValueStep = (
  t: TFunction,
): FormDefinition<ApplicationFormValues> => {
  return {
    title: t("market_value_title"),
    description: t("market_value_description"),
    submit: true,
    fields: [
      {
        label: t("market_value_label"),
        placeholder: t("market_value_placeholder"),
        component: "NUMBER_INPUT",
        name: "marketValue",
        inputMode: "numeric",
        suffix: "€",
        rules: {
          required: {
            value: true,
            message: t("error_market_value"),
          },
          min: 5000,
        },
      },
    ],
  };
};

export const getZipCodeStep = (
  t: TFunction,
  title: string = "zip_code_title",
  label: string = "zip_code_where_to_buy_label",
): FormDefinition<ApplicationFormValues> => {
  return {
    submit: true,
    title: t(title as any),
    description: t("where_to_buy_description"),
    fields: [
      {
        label: t(label as any),
        component: "INPUT",
        name: "postalCode",
        placeholder: t("zip_code_placeholder"),
        type: "text",
        inputMode: "numeric",
        rules: {
          required: {
            value: true,
            message: t("error_postal_code_required"),
          },
          minLength: {
            value: 5,
            message: t("error_postal_code_format"),
          },
          maxLength: {
            value: 5,
            message: t("error_postal_code_format"),
          },
          validate: validateZip,
        },
      },
    ],
  };
};

export const getConstructionCostsStep = (
  t: TFunction,
): FormDefinition<ApplicationFormValues> => {
  return {
    submit: true,
    title: t("construction_cost_title"),
    description: t("construction_cost_description"),
    fields: [
      {
        component: "NUMBER_INPUT",
        name: "constructionRelatedCosts",
        label: t("construction_cost_label"),
        placeholder: t("construction_cost_placeholder"),
        inputMode: "numeric",
        suffix: "€",
        rules: {
          required: {
            value: true,
            message: t("error_construction_costs"),
          },
          min: 5000,
        },
      },
    ],
  };
};

export const getLoanAmountStep = (
  t: TFunction,
): FormDefinition<ApplicationFormValues> => {
  return {
    submit: true,
    title: t("loan_amount_title"),
    description: t("loan_amount_description"),
    fields: [
      {
        label: t("loan_amount_label"),
        placeholder: t("loan_amount_placeholder"),
        component: "NUMBER_INPUT",
        name: "loanAmount",
        inputMode: "numeric",
        suffix: "€",
        rules: {
          required: { value: true, message: t("error_loan_amount") },
          min: 25000,
        },
      },
    ],
  };
};

export const getLoanDateStep = (
  t: TFunction,
): FormDefinition<ApplicationFormValues> => {
  return {
    submit: true,
    title: t("loan_date_title"),
    description: t("loan_date_description"),
    fields: [
      {
        label: t("loan_date_label"),
        component: "INPUT",
        name: "loanDate",
        type: "date",
        min: getMinMaxDateInput(),
        // 60 month or 5 years
        max: getMinMaxDateInput(5),
        rules: {
          required: { value: true, message: t("error_loan_date") },
          validate: (value: string) => validateDateInput(value, 5),
        },
      },
    ],
  };
};
